import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import type { User } from '@features/user-management';

export type BootstrapStatus = 'pending' | 'authenticated' | 'anonymous' | 'unreachable';

export type SetCredentialsPayload = {
  username: string;
  token: string;
  companyName?: string;
  tenantId?: string;
  tenantName?: string;
};

export interface AuthenticationState {
  bootstrapStatus: BootstrapStatus;
  username: string | null;
  companyName: string;
  tenantId: string;
  tenantName: string;
  token: string;
  user: User;
}

const initialState: AuthenticationState = {
  bootstrapStatus: 'pending',
  username: null,
  companyName: '',
  tenantId: '',
  tenantName: '',
  token: '',
  user: {} as User,
};

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<SetCredentialsPayload>) => {
      const { username, token, companyName, tenantId, tenantName } = action.payload;
      state.username = username;

      /* If companyName, tenantId, and tenantName are not defined, it means the user does not have multi-tenant so we are setting these values to empty strings */
      state.companyName = companyName ?? '';
      state.tenantId = tenantId ?? '';
      state.tenantName = tenantName ?? '';
      state.token = token;
      state.bootstrapStatus = 'authenticated';
    },
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setBootstrapStatus: (state, action: PayloadAction<BootstrapStatus>) => {
      state.bootstrapStatus = action.payload;
    },
    logout: (): AuthenticationState => {
      // We set the status to 'anonymous' (instead of 'pending') so the app
      // knows we are definitively logged out. This prevents the app from
      // getting stuck on a loading spinner and sends the user straight to the Login screen.
      return { ...initialState, bootstrapStatus: 'anonymous' };
    },
  },
});

export const { setCredentials, setAuthToken, logout, setUser, setBootstrapStatus } = authenticationSlice.actions;

/**
 * Note: We define a minimal `AuthSliceState` type here instead of importing `RootState`.
 * If we imported `RootState` from the main store file, it would create a circular dependency
 * (Store imports Slice -> Slice imports Store -> Crash!).
 * Redux's `useAppSelector` is smart enough to understand this minimal type automatically.
 */
type AuthSliceState = { authentication: AuthenticationState };

export const selectBootstrapStatus = (state: AuthSliceState): BootstrapStatus => state.authentication.bootstrapStatus;

export const selectIsAuthenticated = (state: AuthSliceState): boolean =>
  state.authentication.bootstrapStatus === 'authenticated';

// This acts as a "flare gun". It is fired by the API when a token refresh completely fails.
// It doesn't update the state directly. Instead, our background listener sees this flare
// and triggers the full, safe logout process.
export const authExpired = createAction('authentication/authExpired');

export const authenticationReducer = authenticationSlice.reducer;
